import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { BlockEditModal } from "../BlockEditModal";
import { sanitizeToHtml } from "@/components/preview/BlockRenderer";
import styles from "../blocks/blocks.module.scss";

type AnnotationType = "correction" | "suggestion" | "highlight" | "comment";
type AnnotationStatus = "pending" | "accepted" | "rejected";

const ANNOTATION_TYPES: AnnotationType[] = ["correction", "suggestion", "highlight", "comment"];
const ANNOTATION_STATUSES: AnnotationStatus[] = ["pending", "accepted", "rejected"];

const ANNOTATION_LABELS: Record<AnnotationType, string> = {
  correction: "Correction",
  suggestion: "Suggestion",
  highlight: "Highlight",
  comment: "Comment",
};

const ANNOTATION_COLORS: Record<AnnotationType, string> = {
  correction: "#dc2626",
  suggestion: "#2563eb",
  highlight: "#d97706",
  comment: "#6b7280",
};

const STATUS_LABELS: Record<AnnotationStatus, string> = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
};

const STATUS_COLORS: Record<AnnotationStatus, string> = {
  pending: "#d97706",
  accepted: "#16a34a",
  rejected: "#dc2626",
};

export function AnnotationNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const rawType = node.attrs.annotationType as string;
  const annotationType: AnnotationType = ANNOTATION_TYPES.includes(rawType as AnnotationType)
    ? (rawType as AnnotationType)
    : "comment";
  const rawStatus = node.attrs.status as string;
  const status: AnnotationStatus = ANNOTATION_STATUSES.includes(rawStatus as AnnotationStatus)
    ? (rawStatus as AnnotationStatus)
    : "pending";
  const text = (node.attrs.text as string) || "";
  const originalContent = (node.attrs.originalContent as string) || "";

  const color = ANNOTATION_COLORS[annotationType];

  const [isOpen, setIsOpen] = useState(false);
  const [draftType, setDraftType] = useState<AnnotationType>(annotationType);
  const [draftStatus, setDraftStatus] = useState<AnnotationStatus>(status);
  const [draftText, setDraftText] = useState(text);
  const [draftOriginal, setDraftOriginal] = useState(originalContent);

  const handleOpen = useCallback(() => {
    setDraftType(annotationType);
    setDraftStatus(status);
    setDraftText(text);
    setDraftOriginal(originalContent);
    setIsOpen(true);
  }, [annotationType, status, text, originalContent]);

  const handleSave = useCallback(() => {
    updateAttributes({
      annotationType: draftType,
      status: draftStatus,
      text: draftText,
      originalContent: draftOriginal,
    });
    setIsOpen(false);
  }, [draftType, draftStatus, draftText, draftOriginal, updateAttributes]);

  const handleStatusChange = useCallback((newStatus: AnnotationStatus) => {
    updateAttributes({ status: newStatus });
  }, [updateAttributes]);

  return (
    <NodeViewWrapper data-type="annotation-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div
        className={styles.annotationBlock}
        style={{ borderLeftColor: color }}
      >
        <div className={styles.annotationHeader}>
          <span className={styles.annotationBadge} style={{ background: color }}>{ANNOTATION_LABELS[annotationType]}</span>
          <span className={styles.annotationStatusBadge} style={{ background: STATUS_COLORS[status] }}>{STATUS_LABELS[status]}</span>
          <div className={styles.annotationActions}>
            {status !== "accepted" && (
              <button
                className={styles.annotationStatusBtn}
                onClick={(e) => { e.stopPropagation(); handleStatusChange("accepted"); }}
                title="Accept"
                style={{ color: "#16a34a" }}
              >
                Accept
              </button>
            )}
            {status !== "rejected" && (
              <button
                className={styles.annotationStatusBtn}
                onClick={(e) => { e.stopPropagation(); handleStatusChange("rejected"); }}
                title="Reject"
                style={{ color: "#dc2626" }}
              >
                Reject
              </button>
            )}
          </div>
        </div>
        {originalContent && (
          <div className={styles.annotationOriginal}>
            <span className={styles.annotationOriginalLabel}>Original:</span>
            <span className={styles.annotationOriginalText}>{originalContent}</span>
          </div>
        )}
        {text ? (
          <div
            className={styles.annotationContent}
            onClick={handleOpen}
            style={{ cursor: "pointer" }}
            title="Click to edit annotation"
            dangerouslySetInnerHTML={{ __html: sanitizeToHtml(text) }}
          />
        ) : (
          <div
            className={styles.annotationContent}
            onClick={handleOpen}
            style={{ cursor: "pointer" }}
            title="Click to edit annotation"
          >
            <span style={{ color: "var(--lilia-text-muted, #999)" }}>Click to add annotation...</span>
          </div>
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title={`Edit ${ANNOTATION_LABELS[draftType]} Annotation`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={640}
      >
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Type</label>
            <Select value={draftType} onValueChange={(val) => setDraftType((val || "comment") as AnnotationType)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANNOTATION_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{ANNOTATION_LABELS[type]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Status</label>
            <Select value={draftStatus} onValueChange={(val) => setDraftStatus((val || "pending") as AnnotationStatus)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANNOTATION_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Original Content (optional)</label>
          <input
            type="text"
            value={draftOriginal}
            onChange={(e) => setDraftOriginal(e.target.value)}
            placeholder="Original text being annotated..."
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Annotation Text</label>
          <textarea
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder="Enter annotation text..."
            rows={6}
            autoFocus
            style={{
              width: "100%", padding: "12px", fontSize: 13,
              border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
              resize: "vertical", lineHeight: 1.6,
            }}
          />
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
